import { UsersService } from './../../../users/users.service';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AdminService } from '../../admin.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { TranslateService } from '@ngx-translate/core';
import { DeviceFactory } from 'src/app/home/site-dashboard/device/device-factory/device-factory';

@Component({
	selector: 'app-admin-actions',
	templateUrl: './admin-actions.component.html',
	styleUrls: ['./admin-actions.component.css'],
})
export class AdminActionsComponent implements OnInit {
	readonly commandsEnum = {
		generateDailyDetails: 'generateDailyDetails',
		generateDeviceAnalytics: 'generateDeviceAnalytics',
		generateSiteData: 'generateSiteData',
		generateMaintenanceSiteReport: 'generateMaintenanceSiteReport',
		generateIMDeviceAnalytics: 'generateIMDeviceAnalytics',
	};

	commands: {key: string, text: string}[] = [
		{ key: this.commandsEnum.generateDailyDetails, text: this.translateService.instant('devices.regenerate_daily_details') },
		{ key: this.commandsEnum.generateDeviceAnalytics, text: 'Generate Device Analytics' },
		{ key: this.commandsEnum.generateSiteData, text: 'Generate Site Reports' },
		{ key: this.commandsEnum.generateMaintenanceSiteReport, text: 'Generate Maintenance Site Report' },
		{ key: this.commandsEnum.generateIMDeviceAnalytics, text: 'Generate IM Device Analytics' },
	];

	// Which pair of analytics tables every read path serves from. The old generation fills
	// device_analytics_data/_reports_data, the event-sourced one device_analytics_summary/_monthly.
	readonly analyticsSourceOptions: {key: 'old' | 'new', text: string}[] = [
		{ key: 'old', text: 'Old' },
		{ key: 'new', text: 'New' },
	];

	analyticsSource: 'old' | 'new';
	savedAnalyticsSource: 'old' | 'new';

	// Which devices report their serving cell to the Cell Info Service on connect. Defaults to
	// disabled; 'static_devices' acts only on the MACs listed below it.
	readonly estimatedLocationModes = {
		disabled: 'disabled',
		staticDevices: 'static_devices',
		all: 'all',
	};

	readonly estimatedLocationModeOptions: {key: string, text: string}[] = [
		{ key: this.estimatedLocationModes.disabled, text: 'Disabled' },
		{ key: this.estimatedLocationModes.staticDevices, text: 'Enabled For Specific Devices' },
		{ key: this.estimatedLocationModes.all, text: 'Enabled For All Devices' },
	];

	estimatedLocationMode: string;
	estimatedLocationDevices = '';
	savedEstimatedLocationFlag = { mode: '', macAddresses: '' };

	command: string;
	macAddress: string;
	siteId: string;
	fileName: string;
	excludeLargeSites: boolean;
	deviceTypes: number[] = [];
	deviceTypeOptions = DeviceFactory.options;

	onCommandChange() {
		this.deviceTypes = [];
		this.deviceTypeOptions = this.command === this.commandsEnum.generateIMDeviceAnalytics
			? DeviceFactory.ImeterOptionsType
			: DeviceFactory.options;
	}

	constructor(
		private router: Router,
		public userService: UsersService,
		private adminService: AdminService,
		private notificationMessageService: NotificationMessageService,
		private translateService: TranslateService
	) {
	}

	ngOnInit() {
		if(!this.userService.hasAccessFunction('sched_functions_control') && !this.userService.hasAccessFunction('super_admin', 'write'))
			return this.router.navigate(['/unauthorized']);

		if(this.userService.hasAccessFunction('super_admin', 'write')) {
			this.getAnalyticsSource();
			this.getEstimatedLocationFlag();
		}
	}

	getAnalyticsSource() {
		this.adminService.getAnalyticsSource().subscribe((res: any) => {
			this.analyticsSource = res?.source;
			this.savedAnalyticsSource = res?.source;
		});
	}

	saveAnalyticsSource() {
		this.adminService.setAnalyticsSource(this.analyticsSource).subscribe((res: any) => {
			this.savedAnalyticsSource = res?.source;
			this.notificationMessageService.setMessage('globalSuccessMsg', {clearOnXTimeNavigate: 1});
		});
	}

	getEstimatedLocationFlag() {
		this.adminService.getEstimatedLocationFlag().subscribe((res: any) => {
			this.estimatedLocationMode = res?.mode;
			this.estimatedLocationDevices = (res?.macAddresses || []).join(', ');
			this.savedEstimatedLocationFlag = { mode: this.estimatedLocationMode, macAddresses: this.estimatedLocationDevices };
		});
	}

	// The device list only applies to 'static_devices'; in the other modes the field stays visible
	// but disabled, and the server ignores whatever it holds.
	estimatedLocationHasDevicesOption() {
		return this.estimatedLocationMode === this.estimatedLocationModes.staticDevices;
	}

	parseEstimatedLocationDevices() {
		return (this.estimatedLocationDevices || '').split(',')
			.map(mac => mac.trim().replace(/[:-]/g, ''))
			.filter(mac => mac.length > 0);
	}

	// Only complains about text the admin actually typed. An empty list is not valid either, but it
	// blocks Save rather than showing an error on a field nobody has filled in yet.
	estimatedLocationDevicesHasError() {
		if(!this.estimatedLocationHasDevicesOption())
			return false;

		return this.parseEstimatedLocationDevices().some(mac => !/^[a-fA-F0-9]{12}$/.test(mac));
	}

	validateEstimatedLocationDevices() {
		if(!this.estimatedLocationHasDevicesOption())
			return true;

		const macAddresses = this.parseEstimatedLocationDevices();

		return macAddresses.length > 0 && !this.estimatedLocationDevicesHasError();
	}

	estimatedLocationHasChanges() {
		return this.estimatedLocationMode !== this.savedEstimatedLocationFlag.mode
			|| (this.estimatedLocationHasDevicesOption() && this.estimatedLocationDevices !== this.savedEstimatedLocationFlag.macAddresses);
	}

	saveEstimatedLocationFlag() {
		if(!this.validateEstimatedLocationDevices()) {
			this.notificationMessageService.setMessage('translate|admin.estimated_location_invalid_devices');
			return;
		}

		this.adminService.setEstimatedLocationFlag(this.estimatedLocationMode, this.estimatedLocationDevices).subscribe((res: any) => {
			this.estimatedLocationDevices = (res?.macAddresses || []).join(', ');
			this.savedEstimatedLocationFlag = { mode: res?.mode, macAddresses: this.estimatedLocationDevices };
			this.notificationMessageService.setMessage('globalSuccessMsg', {clearOnXTimeNavigate: 1});
		});
	}

	forceWebsiteRefresh() {
		this.adminService.forceWebsiteRefresh().subscribe(() => {
			this.notificationMessageService.setMessage('globalSuccessMsg',{clearOnXTimeNavigate: 1});
		});
	}

	commandHasMacOption() {
		return [this.commandsEnum.generateDailyDetails, this.commandsEnum.generateDeviceAnalytics].includes(this.command);
	}

	commandHasSiteOption() {
		return [this.commandsEnum.generateSiteData, this.commandsEnum.generateMaintenanceSiteReport, this.commandsEnum.generateDailyDetails, this.commandsEnum.generateDeviceAnalytics, this.commandsEnum.generateIMDeviceAnalytics].includes(this.command);
	}

	commandHasExcludeSitesOption() {
		return [this.commandsEnum.generateDailyDetails, this.commandsEnum.generateDeviceAnalytics].includes(this.command);
	}

	commandHasDeviceTypeOption() {
		return [this.commandsEnum.generateDailyDetails, this.commandsEnum.generateDeviceAnalytics, this.commandsEnum.generateIMDeviceAnalytics].includes(this.command);
	}

	validateMacAddress() {
		let input = this.macAddress;

		//trim input
		input = input ? input.trim() : '';

		if(!input)
			return true;

		input = input.trim().replace(/:/g, "");
		if(!/^[a-fA-F0-9]{12}$/.test(input))
			return false;

		return true;
	}

	validateSiteId() {
		let input = this.siteId;

		//trim input
		input = input ? input.trim() : '';

		if(!input)
			return true;

		if(!Number.isInteger(+input) || +input < 0)
			return false;

		return true;
	}

	sendGenerateCommand() {
		if(this.commandHasMacOption() && !this.validateMacAddress()) {
			this.notificationMessageService.setMessage('translate|generate_script.invalidMac');
			return;
		}

		if(this.commandHasSiteOption() && !this.validateSiteId()) {
			this.notificationMessageService.setMessage('translate|generate_script.invalidSite');
			return;
		}

		this.adminService.generateScript(this.command, this.macAddress, this.siteId, this.fileName, this.excludeLargeSites, this.deviceTypes).subscribe(() => {
			this.notificationMessageService.setMessage('globalSuccessMsg',{clearOnXTimeNavigate: 1});
		});
	}
}